import { forwardRef, ForwardRefRenderFunction, useEffect, useState } from "react";
//#region types
import { BenefitFormTypes } from "models/formTypes";
import { BenefitUpdateFormContainerProps } from "models/propTypes/benefitsPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { BenefitsEnums } from "models/enums";
import { FormFieldConstants } from "utils/constants";
//#endregion
//#region components
import { LoadingWithLogo } from "components/common";
import { useBenefitService, useWintSalaryService } from "serviceHooks";
import BenefitUpdateForm from "./benefitUpdateForm";
import useBenefitTypesHelper from "./useBenefitTypesHelper";
import { useDeviationPermissions } from "components/salaryDeviation";
import * as Yup from "yup";
//#endregion
import { DateHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const BenefitUpdateFormContainer: ForwardRefRenderFunction<FormHandles, BenefitUpdateFormContainerProps> = (
  props,
  ref
) => {
  const { benefitId, readonly } = props;

  const { getBenefitById, updateBenefitReapprove } = useBenefitService();
  const { frequencyChoices } = useBenefitTypesHelper();

  const { isSalaryAdmin, isBusinessUnitManagerWithSalaryRights, isDeviationAdmin } = useDeviationPermissions();
  const { searchPersonsAutoComplete } = useWintSalaryService();

  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<BenefitFormTypes.BenefitUpdate | undefined>(undefined);
  const [availableEmployees, setAvailableEmployees] = useState<AutocompleteListItem[]>([]);

  const firstDayOfMonth = DateHelper.getFirstDayOfMonth(DateHelper.today());

  const validationSchema = Yup.object<BenefitFormTypes.BenefitUpdate>({
    name: Yup.string().required(TranslationKeys.Validations.Required),
    benefitValue: Yup.number().required(TranslationKeys.Validations.Required),
    benefitAmountEffectFrom: Yup.date().nullable().min(firstDayOfMonth, TranslationKeys.Validations.Min),
    employees: Yup.array()
      .of(Yup.object<AutocompleteListItem>())
      .min(1, TranslationKeys.Validations.Required)
      .required(TranslationKeys.Validations.Required),
    frequency: Yup.mixed()
      .oneOf(Object.values(BenefitsEnums.BenefitFrequency))
      .required(TranslationKeys.Validations.Required),
    employeeDetails: Yup.array()
      .of(
        Yup.object<BenefitFormTypes.EmployeeDetails>({
          user: Yup.mixed().required(TranslationKeys.Validations.Required),
          dateRange: Yup.object({
            start: Yup.date().nullable(),
            end: Yup.date()
              .nullable()
              .when(FormFieldConstants.Util.DateRange.Start, ([start], schema) =>
                start instanceof Date && !isNaN(start.getTime())
                  ? schema.min(start, TranslationKeys.Validations.EndDateMustBeLaterThanStartDate)
                  : schema
              ),
          }),
        })
      )
      .min(1, TranslationKeys.Validations.Required)
      .required(TranslationKeys.Validations.Required),
  });

  useEffect(() => {
    const loadBenefit = async () => {
      setLoading(true);
      try {
        if (benefitId) {
          const [benefit, allEmployees] = await Promise.all([
            getBenefitById(benefitId),
            searchPersonsAutoComplete(
              "",
              false,
              true,
              isBusinessUnitManagerWithSalaryRights && !(isSalaryAdmin || isDeviationAdmin)
            ),
          ]);

          const employeesWithLockedState = allEmployees.map(
            (employee): AutocompleteListItem => ({
              ...employee,
              locked: benefit.employees?.some((e) => e.id === employee.id),
            })
          );
          setAvailableEmployees(employeesWithLockedState);
          setInitialValues(benefit);
        }
      } catch (error) {
        console.error("Failed to load benefit:", error);
      } finally {
        setLoading(false);
      }
    };

    loadBenefit();
  }, [benefitId]);

  const handleSubmit = async (values: BenefitFormTypes.BenefitUpdate) => {
    const result = await updateBenefitReapprove(benefitId, values);
    return result.data;
  };

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <BenefitUpdateForm
        ref={ref}
        {...props}
        defaultValues={initialValues}
        frequencyChoices={frequencyChoices}
        availableEmployees={availableEmployees}
        handleSubmit={handleSubmit}
        validationSchema={validationSchema}
        readonly={readonly}
      />
    </>
  );
};

export default forwardRef(BenefitUpdateFormContainer);
